import axios from "axios";
import {
  apiClient,
  createIdempotencyKey,
} from "../../shared/api/httpClient.js";

export async function listMyOrders(params = {}) {
  const response = await apiClient.get("/quick-orders/mine/", { params });
  return response.data;
}

export async function listOpenQuickOrders({ orderNo = "", cursor = "", pageSize = 20 } = {}) {
  const response = await apiClient.get("/quick-orders/open/", {
    params: {
      ...(orderNo.trim() ? { order_no: orderNo.trim().toUpperCase() } : {}),
      ...(cursor ? { cursor } : {}),
      page_size: pageSize,
    },
  });
  return response.data;
}

export async function getOpenQuickOrderPreview(orderId) {
  const response = await apiClient.get(`/quick-orders/open/${orderId}/`);
  return response.data;
}

export async function createQuickOrder(payload) {
  const response = await apiClient.post("/quick-orders/", payload, {
    headers: { "Idempotency-Key": createIdempotencyKey("quick-order") },
  });
  return response.data;
}

function wait(milliseconds) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });
}

async function uploadTradeMedia(file, purpose, onProgress) {
  const initResponse = await apiClient.post(
    "/trade/media-uploads/",
    {
      filename: file.name,
      content_type: file.type,
      file_size: file.size,
      purpose,
    },
    {
      headers: { "Idempotency-Key": createIdempotencyKey("trade-media") },
    },
  );
  const session = initResponse.data;
  const uploadBody = new FormData();
  Object.entries(session.upload?.fields || {}).forEach(([key, value]) => {
    uploadBody.append(key, value);
  });
  uploadBody.append("file", file);

  await axios.post(session.upload.url, uploadBody, {
    timeout: 120_000,
    onUploadProgress: (event) => {
      if (!event.total) return;
      onProgress?.(Math.round((event.loaded / event.total) * 80));
    },
  });
  onProgress?.(82);

  let statusResponse = await apiClient.post(
    `/trade/media-uploads/${session.upload_id}/finalize/`,
    {
      upload_token: session.upload_token,
      object_key: session.object_key,
    },
  );

  for (let attempt = 0; attempt < 30; attempt += 1) {
    const payload = statusResponse.data;
    if (payload.status === "BOUND" && payload.asset?.id) {
      onProgress?.(100);
      return payload.asset;
    }
    if (payload.status === "FINALIZE_REJECTED") {
      throw new Error("素材文件校验失败，请重新选择文件");
    }
    await wait(800);
    statusResponse = await apiClient.get(
      `/trade/media-uploads/${session.upload_id}/`,
    );
    onProgress?.(Math.min(98, 84 + attempt));
  }

  throw new Error("素材处理超时，请稍后重试");
}

export async function uploadQuickOrderMaterial(file, onProgress) {
  return uploadTradeMedia(file, "QUICK_ORDER_MATERIAL", onProgress);
}

export async function uploadQuickOrderDeliveryFile(file, onProgress) {
  return uploadTradeMedia(file, "QUICK_ORDER_DELIVERY", onProgress);
}

export async function getQuickOrder(orderId) {
  const response = await apiClient.get(`/quick-orders/${orderId}/`);
  return response.data;
}

export async function grabQuickOrder(orderId) {
  const response = await apiClient.post(
    `/quick-orders/${orderId}/grab/`,
    {},
    {
      headers: { "Idempotency-Key": createIdempotencyKey("quick-order-grab") },
    },
  );
  return response.data;
}

export async function confirmQuickOrderProvider(orderId, payload) {
  const response = await apiClient.post(
    `/quick-orders/${orderId}/provider-confirm/`,
    payload,
    {
      headers: { "Idempotency-Key": createIdempotencyKey("quick-order-provider-confirm") },
    },
  );
  return response.data;
}

export async function submitQuickOrderDelivery(orderId, payload) {
  const response = await apiClient.post(
    `/quick-orders/${orderId}/deliveries/`,
    payload,
    {
      headers: { "Idempotency-Key": createIdempotencyKey("quick-order-delivery") },
    },
  );
  return response.data;
}

export async function listQuickOrderDeliveries(orderId) {
  const response = await apiClient.get(`/quick-orders/${orderId}/deliveries/`);
  return response.data;
}

export async function requestQuickOrderRevision(orderId, payload) {
  const response = await apiClient.post(
    `/quick-orders/${orderId}/revisions/`,
    payload,
    {
      headers: { "Idempotency-Key": createIdempotencyKey("quick-order-revision") },
    },
  );
  return response.data;
}

export async function createTradeMediaDownloadUrl(assetId) {
  const response = await apiClient.post(`/trade/media-assets/${assetId}/download-url/`);
  return response.data;
}

export async function createOrderPayment(orderId, payload = {}) {
  const idempotencyKey = getOrderPaymentIdempotencyKey(orderId);
  const response = await apiClient.post(
    `/quick-orders/${orderId}/payments/`,
    payload,
    {
      headers: { "Idempotency-Key": idempotencyKey },
    },
  );
  return response.data;
}

export function getOrderPaymentIdempotencyKey(orderId) {
  const storageKey = `tupi:quick-order-payment-key:${orderId}`;
  try {
    const existingKey = window.localStorage.getItem(storageKey);
    if (existingKey) return existingKey;
    const nextKey = createIdempotencyKey("quick-order-payment");
    window.localStorage.setItem(storageKey, nextKey);
    return nextKey;
  } catch {
    return createIdempotencyKey("quick-order-payment");
  }
}

export async function getOrderPaymentSummary(orderId) {
  const response = await apiClient.get(`/quick-orders/${orderId}/payments/`);
  return response.data;
}

/**
 * Extracts the browser redirect URL returned by a payment adapter.
 * Alipay page-pay responses expose `payment_payload.redirect_url`; the
 * fallback composition keeps the client compatible with adapters that return
 * a gateway URL and query separately.
 */
export function getPaymentRedirectUrl(paymentResponse) {
  const payment = paymentResponse?.payment || paymentResponse || {};
  const payload = payment.payment_payload || payment.payload || {};
  const directUrl = payload.redirect_url
    || payload.url
    || payment.redirect_url
    || payment.payment_url
    || payment.pay_url;
  if (typeof directUrl === "string" && /^https?:\/\//i.test(directUrl)) {
    return directUrl;
  }

  const gatewayUrl = payload.gateway_url || payload.gatewayUrl;
  const query = payload.query;
  if (typeof gatewayUrl === "string" && /^https?:\/\//i.test(gatewayUrl) && query) {
    return `${gatewayUrl}${gatewayUrl.includes("?") ? "&" : "?"}${query}`;
  }
  return "";
}

export async function acceptQuickOrder(orderId, acceptanceNote = "确认交付物符合验收标准") {
  const response = await apiClient.post(
    `/quick-orders/${orderId}/accept/`,
    { acceptance_note: acceptanceNote },
    {
      headers: { "Idempotency-Key": createIdempotencyKey("quick-order-accept") },
    },
  );
  return response.data;
}

export async function closeQuickOrder(orderId, payload = "双方协商结束") {
  const requestBody = typeof payload === "string"
    ? { closure_reason: payload }
    : {
      closure_reason: payload?.closure_reason || payload?.closureReason || "双方协商结束",
      ...(payload?.refund_amount_yuan !== undefined && payload?.refund_amount_yuan !== ""
        ? { refund_amount_yuan: payload.refund_amount_yuan }
        : {}),
    };
  const response = await apiClient.post(
    `/quick-orders/${orderId}/close/`,
    requestBody,
    {
      headers: { "Idempotency-Key": createIdempotencyKey("quick-order-close") },
    },
  );
  return response.data;
}
